import ModuleShell from "../../components/director/module1/ModuleShell";
import Money from "../../components/director/page/Money";
import {
  FRESHNESS_LABELS,
  ageText,
  blockedReasonSentence,
  dataAgeDays,
  jobStatusLabel,
  originLabel,
  skipReasonLabel,
} from "../../components/director/page/freshness";
import { useDirectorContext } from "@contexts/director";
import {
  brtDateKey,
  compactCents,
  formatDayMonth,
  formatTimeOfDay,
} from "../../utils/directorFormat";

const UNMAPPED_CODES = new Set(["unmapped_cost_center", "unmapped_cost_centers"]);
const QUOTA_CODES = new Set(["source_rate_limited", "sienge_quota_blocked"]);

const isCents = (value) => Number.isSafeInteger(value);

// "hoje 06:00" ou "03/11 16:10", em Brasília.
const whenText = (value, now) => {
  const day = formatDayMonth(value);
  const time = formatTimeOfDay(value);
  if (!day) return "—";
  const label = brtDateKey(value) === brtDateKey(now) ? "hoje" : day;
  return time ? `${label} ${time}` : label;
};

const durationText = (cycle) => {
  let seconds = Number.isFinite(cycle?.durationSeconds) ? cycle.durationSeconds : null;
  if (seconds === null && Number.isFinite(cycle?.durationMs)) seconds = cycle.durationMs / 1000;
  if (seconds === null) {
    const start = Date.parse(cycle?.startedAt ?? cycle?.started_at ?? "");
    const end = Date.parse(cycle?.completedAt ?? cycle?.completed_at ?? "");
    if (!Number.isNaN(start) && !Number.isNaN(end) && end >= start) seconds = (end - start) / 1000;
  }
  if (seconds === null) return "—";
  if (seconds < 60) return `${Math.max(1, Math.round(seconds))} s`;
  return `${Math.round(seconds / 60)} min`;
};

const cycleOrigin = (cycle) => {
  const origins = Array.isArray(cycle?.origins) && cycle.origins.length ? cycle.origins : [cycle?.origin].filter(Boolean);
  return origins.length ? origins.map(originLabel).join(" + ") : "—";
};

const cycleStatus = (cycle) => {
  const label = jobStatusLabel(cycle?.status);
  const skip = cycle?.status === "skipped" ? skipReasonLabel(cycle.skipReason ?? cycle.skip_reason ?? cycle.reasonCode) : null;
  if (skip) return `${label} (${skip})`;
  if (cycle?.status === "succeeded" && cycle?.published === false) return `${label} (validado, sem publicar)`;
  return label;
};

// Ação recomendada (spec `director/data-freshness`): centros sem mapeamento → completar o
// portfólio no backoffice; cota do Sienge → aguardar o horário indicado.
const recommendedAction = (status, unmapped) => {
  const code = status?.lastAttempt?.reasonCode;
  if (unmapped.length > 0 || UNMAPPED_CODES.has(code)) {
    return "Complete o portfólio no Backoffice › Governança Financeira, mapeando os centros de custo abaixo. "
      + "A próxima atualização (agendada ou pelo botão) publica os dados.";
  }
  if (QUOTA_CODES.has(code)) {
    const until = status?.siengeQuotaBlockedUntil ?? status?.diagnostic?.siengeQuotaBlockedUntil ?? null;
    const time = formatTimeOfDay(until);
    return time
      ? `Aguarde a renovação da cota do Sienge: uma nova tentativa é possível a partir de ${time} de ${formatDayMonth(until)}.`
      : "Aguarde a renovação da cota diária do Sienge, à meia-noite (horário de Brasília), e tente de novo.";
  }
  return null;
};

/**
 * Estado da atualização (Informações complementares › Dados e governança): o estado dos dados,
 * o motivo da última tentativa que não publicou, os centros de custo sem mapeamento e os últimos
 * ciclos. A lista de centros e os ciclos só chegam para quem lê a governança dos dados.
 */
const RefreshStatus = ({ now }) => {
  const { refreshStatus: status = null, refreshStatusError = null } = useDirectorContext();
  const reference = now ?? new Date();
  const unmapped = [...(status?.diagnostic?.unmappedCostCenters || [])]
    .sort((left, right) => (right?.cents ?? 0) - (left?.cents ?? 0));
  const unmappedCents = unmapped.reduce((total, item) => (isCents(item?.cents) ? total + item.cents : total), 0);
  const attempt = status?.lastAttempt || null;
  const notPublished = attempt && ["blocked", "failed"].includes(attempt.status);
  const action = notPublished ? recommendedAction(status, unmapped) : null;
  const cycles = Array.isArray(status?.recentCycles) ? status.recentCycles : null;
  const governance = Boolean(status?.diagnostic) || cycles !== null;

  return <ModuleShell
    title="Estado da atualização"
    description="Os dados da Diretoria estão em dia? Se não, por quê e o que fazer."
    showProjectFilter={false}
  >
    {!status && !refreshStatusError && <section className="director-state" role="status">Carregando o estado da atualização…</section>}
    {refreshStatusError && <section className="director-state director-state--error" role="alert">
      <strong>{refreshStatusError.message || "Não foi possível ler o estado da atualização."}</strong>
    </section>}
    {status && <>
      <section className="director-section director-refresh-status" aria-label="Situação dos dados">
        <p className="director-workspace__sentence">
          <strong>{FRESHNESS_LABELS[status.state] || "Estado desconhecido"}</strong>
          {` · Dados publicados: ${status.dataAsOf ? whenText(status.dataAsOf, reference) : "nenhum"}`}
          {["stale", "blocked"].includes(status.state) && ageText(dataAgeDays(status, reference))
            ? ` (${ageText(dataAgeDays(status, reference))})`
            : ""}
          {attempt ? ` · Última tentativa: ${whenText(attempt.at, reference)} (${cycleOrigin(attempt)})` : ""}
        </p>
      </section>

      {notPublished && <section className="director-section" aria-labelledby="director-refresh-why">
        <header><div><h2 id="director-refresh-why">Por que não atualizou</h2></div></header>
        <p>
          {`${blockedReasonSentence(status, reference)}.`}
          {unmapped.length > 0 && isCents(unmappedCents) && unmappedCents > 0
            ? ` São ${compactCents(unmappedCents)} em títulos fora do portfólio.`
            : ""}
        </p>
        {action && <p className="director-workspace__warning" role="note"><strong>O que fazer: </strong>{action}</p>}
        {unmapped.length > 0 && <div className="director-table-scroll">
          <table className="director-table director-refresh-status__table" aria-label="Centros de custo sem mapeamento">
            <thead>
              <tr>
                <th scope="col">Centro de custo</th>
                <th scope="col">Entidade</th>
                <th scope="col">Valor</th>
              </tr>
            </thead>
            <tbody>
              {unmapped.map((item) => <tr key={`${item.legalEntityId}-${item.costCenterId}`}>
                <th scope="row">{`${item.costCenterId} · ${item.name || "sem nome no catálogo"}`}</th>
                <td>{item.legalEntityId ?? "—"}</td>
                <td><Money cents={item.cents} /></td>
              </tr>)}
            </tbody>
          </table>
        </div>}
      </section>}

      {cycles && <section className="director-section" aria-labelledby="director-refresh-cycles">
        <header><div><h2 id="director-refresh-cycles">Últimos ciclos</h2></div></header>
        {cycles.length === 0
          ? <p>Nenhum ciclo registrado ainda.</p>
          : <div className="director-table-scroll">
            <table className="director-table director-refresh-status__table" aria-label="Últimos ciclos">
              <thead>
                <tr>
                  <th scope="col">Quando</th>
                  <th scope="col">Origem</th>
                  <th scope="col">Estado</th>
                  <th scope="col">Duração</th>
                </tr>
              </thead>
              <tbody>
                {cycles.map((cycle, index) => <tr key={cycle.id ?? cycle.jobId ?? index}>
                  <th scope="row">{whenText(cycle.at ?? cycle.startedAt ?? cycle.requestedAt, reference)}</th>
                  <td>{cycleOrigin(cycle)}</td>
                  <td>{cycleStatus(cycle)}</td>
                  <td>{durationText(cycle)}</td>
                </tr>)}
              </tbody>
            </table>
          </div>}
      </section>}

      {!governance && <p className="director-owner-footnote">
        A lista de centros de custo e os últimos ciclos aparecem para quem tem acesso à governança dos dados.
      </p>}
    </>}
  </ModuleShell>;
};

export default RefreshStatus;
